import React, { useState, useEffect } from "react";
import {
  Loader2,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Calendar,
  FileText,
  ChevronDown,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  X,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  EmptyResponse,
  ErrorResponse,
  Survey,
  UserRole,
  Question,
  SurveyWriteBase,
  UpdateSurveyPayload,
} from "../types";
import { getEntities } from "../services/entities";
import { Entity } from "../types";
import {
  createSurvey,
  deleteSurvey,
  getSurveys,
  getSurveyById,
  updateSurvey,
} from "../services/surveys";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Notification, { NotificationAction } from "./Notification";
import { translateTextToAll } from "../services/translations";
import { getQuestions } from "../services/questions";
import Header from "./Header";
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";

export default function Surveys() {
  const { user, language, translations, token } = useAuth();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const autoOpenCreateForm = searchParams.get("create") === "true";
  
  const surveyId = params.id;
  const isEditMode = location.pathname.endsWith("/edit");
  
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<Survey | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "draft" | "published" | "archived"
  >("all");

  const [expandedSurveys, setExpandedSurveys] = useState<Set<string>>(
    new Set()
  );

  const queryClient = useQueryClient();

  const {
    data: surveysRaw,
    isLoading,
    isFetching,
    error,
  } = useQuery<Survey[]>({
    queryKey: ["surveys"],
    queryFn: () => getSurveys(token!),
    enabled: !!token,
  });

  // Refrescar listado cuando se vuelve a la vista de listado (sin surveyId)
  useEffect(() => {
    if (!surveyId && !autoOpenCreateForm && token) {
      // Invalidar y refrescar el listado cuando se vuelve al listado
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
    }
  }, [surveyId, autoOpenCreateForm, token, queryClient]);

  // Load specific survey from backend when viewing/editing
  const {
    data: surveyByIdData,
    isLoading: isLoadingSurvey,
  } = useQuery<Survey | ErrorResponse>({
    queryKey: ["survey", surveyId],
    queryFn: () => getSurveyById(surveyId!, token!),
    enabled: !!token && !!surveyId,
  });

  // Find the survey from the URL ID
  const surveys = (surveysRaw || [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  // Use fresh data from backend if available, otherwise fallback to cached data
  const editingItem = surveyId 
    ? (surveyByIdData && !("error" in surveyByIdData) ? surveyByIdData : surveys.find(s => s.id === surveyId) || null)
    : null;
  const showCreateForm = autoOpenCreateForm || !!surveyId;
  // Determine mode: create if no surveyId and autoOpenCreateForm, edit if isEditMode, show if viewing, otherwise create
  const editOrShowMode = surveyId 
    ? (isEditMode ? "edit" : "show")
    : (autoOpenCreateForm ? "create" : "create");

  useEffect(() => {
    // If we navigate to a survey detail but it doesn't exist, redirect to list
    if (surveyId && !isLoadingSurvey && !isLoading && surveyByIdData && "error" in surveyByIdData) {
      navigate("/surveys", { replace: true });
    }
    // If user is Organization and tries to edit a generic survey, redirect to view mode
    // Organization can edit entity surveys but only view generic surveys
    if (surveyId && isEditMode && user?.role === "Organization" && editingItem?.type === "generic") {
      navigate(`/surveys/${surveyId}`, { replace: true });
    }
  }, [surveyId, isLoading, isLoadingSurvey, surveyByIdData, editingItem, navigate, isEditMode, user?.role]);
  const sortQuestionsByTypeThenOrder = (questions: Question[]): Question[] =>
    [...questions].sort((a, b) => {
      if (a.isGeneric && !b.isGeneric) return -1;
      if (!a.isGeneric && b.isGeneric) return 1;
      return (a.order ?? 0) - (b.order ?? 0);
    });

  const handleNavigateToEntities = () => {
    navigate("/entities?create=true");
  };
  const toggleSurveyExpansion = (surveyId: string) => {
    const newExpanded = new Set(expandedSurveys);
    if (newExpanded.has(surveyId)) {
      newExpanded.delete(surveyId);
    } else {
      newExpanded.add(surveyId);
    }
    setExpandedSurveys(newExpanded);
  };

  const [notification, setNotification] = useState<{
    type: "success" | "error" | "warning" | "info";
    title: string;
    message?: string;
    actions?: NotificationAction[];
  } | null>(null);

  const filteredSurveys = surveys.filter((survey) => {
    const matchesSearch =
      !searchTerm.trim() ||
      survey.title[language]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (survey.organizationName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ??
        false);
    const matchesStatus =
      filterStatus === "all" || survey.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      published: { color: "green", label: "Publicada" },
      draft: { color: "yellow", label: "Borrador" },
      archived: { color: "gray", label: "Archivada" },
    };
    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
      >
        {config.label}
      </span>
    );
  };

  const handleDeleteSurvey = async (survey: Survey) => {
    setIsDeleting(true);
    setDeletingId(survey.id);
    try {
      const response = await deleteSurvey(survey, token!);
      if ("error" in response) {
        setNotification({
          type: "error",
          title: translations.errorDeletingSurvey,
          message: translations.unexpectedError,
        });
        return response;
      }
      // Refrescar lista
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      // Mostrar éxito
      setNotification({
        type: "success",
        title: translations.surveyDeletedSuccessfully,
      });
      return response;
    } catch {
      // Por si acaso se lanza alguna excepción no controlada
      setNotification({
        type: "error",
        title: translations.errorDeletingSurvey,
        message: translations.unexpectedError,
      });
      return { error: 500, message: translations.unexpectedError } as ErrorResponse;
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
      setShowConfirmDelete(false);
      setSurveyToDelete(null);
    }
  };

  // Notificación inline global SIEMPRE visible, no solo en showCreateForm
  const notificationComponent = notification && (
    <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
      <Notification
        type={notification.type}
        title={notification.title}
        actions={notification.actions}
        message={notification.message}
        duration={notification.actions?.length ? 0 : 5000}
        onClose={() => setNotification(null)}
      />
    </div>
  );

  // Mostrar estado de carga (carga inicial o recarga al volver al listado)
  if (isLoading || (surveyId && isLoadingSurvey) || (!surveyId && isFetching && !autoOpenCreateForm)) {
    return (
      <>
        {notificationComponent}
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {translations.surveys}
              </h1>
              <p className="text-gray-600 mt-1">
                {translations.loadingSurveys}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">
                {translations.loadingSurveys}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Mostrar estado de error
  if (error) {
    return (
      <>
        {notificationComponent}
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {translations.surveys}
              </h1>
              <p className="text-gray-600 mt-1">
                {translations.errorLoadingSurveys}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <FileText className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {translations.errorLoadingSurveys}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {error instanceof Error
                  ? error.message
                  : translations.unexpectedError}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {translations.retry}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (showCreateForm) {
    // Determine mode based on role and survey type:
    // - Administrator: can edit/create any survey
    // - Organization: can create/edit entity surveys, but only view generic surveys
    let finalMode: "create" | "edit" | "show" = editOrShowMode as "create" | "edit" | "show";
    if (user?.role === "Organization") {
      if (!surveyId) {
        // Organization can create surveys (only entity type)
        finalMode = "create";
      } else {
        // Organization can edit entity surveys, but only view generic surveys
        if (editingItem?.type === "generic") {
          finalMode = "show";
        } else {
          // Allow edit mode for entity surveys
          finalMode = editOrShowMode as "create" | "edit" | "show";
        }
      }
    }
    
    const handleCloseForm = () => {
      // Navegar primero, la invalidación se hará cuando se monte el componente del listado
      navigate("/surveys");
    };
    
    return (
      <>
        {notificationComponent}
        <SurveyForm
          setNotification={setNotification}
          onClose={handleCloseForm}
          item={editingItem}
          mode={finalMode}
          onNavigateToEntities={handleNavigateToEntities}
          sortQuestionsByTypeThenOrder={sortQuestionsByTypeThenOrder}
        />
      </>
    );
  }

  return (
    <>
      {/* Confirm Delete Modal */}
      {showConfirmDelete && surveyToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {translations.confirmDeletion || "Confirmar eliminación"}
            </h3>
            <p className="text-sm text-gray-600 mt-2">
              {translations.areYouSureDelete || "¿Seguro que deseas eliminar esta encuesta?"}
            </p>
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => {
                  setShowConfirmDelete(false);
                  setSurveyToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translations.cancel}
              </button>
              <button
                type="button"
                onClick={() => handleDeleteSurvey(surveyToDelete)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {translations.delete || "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
      {isDeleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl max-w-sm w-full mx-4">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              <p className="text-lg font-medium text-gray-900">
                {translations.deleting || "Eliminando"}
              </p>
              <p className="text-sm text-gray-500 text-center">
                {translations.pleaseWait || translations.loading}
              </p>
            </div>
          </div>
        </div>
      )}
      {notificationComponent}
      <div className="p-6 space-y-6">
        {/* Header */}
        <Header>
          <div className="flex items-center justify-between w-full">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {translations.surveys}
              </h1>
              <p className="text-gray-600 mt-1">{translations.surveys}</p>
            </div>
          </div>
        </Header>

        {/* Action Button and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Add Survey Button - For Administrators and Organizations */}
              {(user?.role === "Administrator" || user?.role === "Organization") && (
                <button
                  onClick={() => navigate("/surveys?create=true")}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex-shrink-0"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {translations.newSurvey}
                </button>
              )}

              {/* Search - takes available space */}
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={translations.searchSurveys}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px]"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as "all" | "draft" | "published" | "archived")}
                >
                  <option value="all">{translations.allStatuses}</option>
                  <option value="draft">{translations.draft}</option>
                  <option value="published">{translations.published}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Surveys Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.title}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.type}
                  </th>
                  {user?.role === "Administrator" && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {translations.organizationColumn}
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.status}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.date}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translations.actions}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSurveys.map((survey) => {
                  const sortedQuestions = sortQuestionsByTypeThenOrder(
                    survey.questions
                  );
                  const isExpanded = expandedSurveys.has(survey.id);
                  return (
                    <React.Fragment key={survey.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-start">
                            <button
                              onClick={() => toggleSurveyExpansion(survey.id)}
                              className="mr-2 mt-1 p-1 hover:bg-gray-200 rounded flex-shrink-0"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 break-words">
                                {survey.title[language]}
                              </div>
                              {survey.description && (
                                <div className="text-sm text-gray-500 break-words mt-1">
                                  {survey.description[language]}
                                </div>
                              )}
                              <div className="text-xs text-gray-400 mt-1">
                                {survey.questions.length}{" "}
                                {translations.questions}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            survey.type === "generic"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }`}>
                            {survey.type === "generic"
                              ? translations.generic
                              : translations.entity}
                          </span>
                        </td>
                        {user?.role === "Administrator" && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {survey.organizationName || "-"}
                          </td>
                        )}

                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(survey.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(survey.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => navigate(`/surveys/${survey.id}`)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title={translations.viewDetails || "Ver detalles"}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {/* Show edit button for Administrators or Organization users editing entity surveys */}
                            {(user?.role === "Administrator" || 
                              (user?.role === "Organization" && survey.type === "entity")) && (
                              <button
                                onClick={() => navigate(`/surveys/${survey.id}/edit`)}
                                className="text-green-600 hover:text-green-900 p-1 rounded"
                                title={translations.editSurvey || "Editar encuesta"}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            {/* Delete button only for Administrators */}
                            {user?.role === "Administrator" && (
                              <button
                                onClick={() => {
                                  setSurveyToDelete(survey);
                                  setShowConfirmDelete(true);
                                }}
                                disabled={isDeleting && deletingId === survey.id}
                                className={`p-1 rounded ${
                                  isDeleting && deletingId === survey.id
                                    ? "text-red-400 cursor-not-allowed"
                                    : "text-red-600 hover:text-red-900"
                                }`}
                                title={translations.delete || "Eliminar"}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td
                            colSpan={user?.role === "Administrator" ? 6 : 5}
                            className="px-6 py-4 bg-gray-50"
                          >
                            <div className="space-y-3">
                              {sortedQuestions.length > 0 ? (
                                <div className="space-y-2">
                                  {sortedQuestions.map((question, index) => (
                                    <div
                                      key={question.id}
                                      className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-gray-200"
                                    >
                                      <div className="flex-shrink-0">
                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                          {index + 1}
                                        </span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900">
                                          {question.text[language]}
                                        </p>
                                        <div className="flex items-center space-x-3 mt-1">
                                          <span
                                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                              !question.isGeneric
                                                ? "bg-green-100 text-green-800"
                                                : "bg-gray-100 text-gray-800"
                                            }`}
                                          >
                                            {question.isGeneric
                                              ? translations.genericQuestion
                                              : translations.specificQuestion}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            {translations.originalOrder}:{" "}
                                            {question.order}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 italic">
                                  {translations.noQuestion}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredSurveys.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {translations.noSurveys}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {translations.startANewSurvey}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

interface SurveyFormProps {
  onClose: () => void;
  mode?: "edit" | "create" | "show";
  item?: Survey | null;
  onNavigateToEntities?: () => void;
  sortQuestionsByTypeThenOrder: (questions: Question[]) => Question[];
}

interface SurveyFormData
  extends Omit<
    Survey,
    | "id"
    | "createdAt"
    | "updatedAt"
    | "title"
    | "description"
    | "questions"
    | "organizationName"
  > {
  title_es: string;
  title_ca: string;
  title_en?: string;
  title_ar?: string;
  description_es?: string;
  description_ca?: string;
  description_en?: string;
  description_ar?: string;
}
type FormData = SurveyFormData;

function SurveyForm({
  onClose,
  setNotification,
  mode = "create",
  item,
  onNavigateToEntities,
  sortQuestionsByTypeThenOrder,
}: SurveyFormProps & { 
  setNotification: (notification: { 
    type: "success" | "error" | "warning" | "info"; 
    title: string; 
    message?: string; 
    actions?: NotificationAction[] 
  } | null) => void;
}) {
  const { user, translations, token, role: roleUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!item && mode === "edit";
  const isViewingOrEditing = mode !== "create";
  
  // Determine initial type: if Organization user, always "entity", otherwise from item or empty
  const initialType = item?.type || (user?.role === "Organization" ? "entity" : "");
  const [surveyTypeSelected, setSurveyTypeSelected] = useState<"generic" | "entity" | "">(
    item 
      ? (item.type as "generic" | "entity") 
      : (mode === "create" && user?.role === "Organization" ? "entity" : "")
  );
  
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<FormData>({
    title_es: item?.title?.es || "",
    title_ca: item?.title?.ca || "",
    title_en: (item?.title as { es: string; ca: string; en?: string; ar?: string })?.en || "",
    title_ar: (item?.title as { es: string; ca: string; en?: string; ar?: string })?.ar || "",
    description_es: item?.description?.es || "",
    description_ca: item?.description?.ca || "",
    description_en: (item?.description as { es: string; ca: string; en?: string; ar?: string })?.en || "",
    description_ar: (item?.description as { es: string; ca: string; en?: string; ar?: string })?.ar || "",
    type: initialType as "generic" | "entity",
    defaultLanguage: item?.defaultLanguage || ("es" as "es" | "ca" | "en" | "ar"),
    organizationId: item?.organizationId || user?.organizationId || "",
    orientation: item?.orientation || ("niño" as "niño" | "familia"),
    objetivo: item?.objetivo || 0,
    status: item?.status || ("draft" as "draft" | "published" | "archived"),
  });

  // Sync fields when editing/showing an existing survey to ensure EN and AR load from API
  useEffect(() => {
    if (!item) return;
    const title = item.title as { es: string; ca: string; en?: string; ar?: string };
    const description = item.description as { es: string; ca: string; en?: string; ar?: string };
    setFormData((prev) => ({
      ...prev,
      title_es: title?.es ?? "",
      title_ca: title?.ca ?? "",
      title_en: title?.en ?? "",
      title_ar: title?.ar ?? "",
      description_es: description?.es ?? "",
      description_ca: description?.ca ?? "",
      description_en: description?.en ?? "",
      description_ar: description?.ar ?? "",
      type: item.type,
      defaultLanguage: item.defaultLanguage,
      organizationId: item.organizationId || user?.organizationId || "",
      orientation: item.orientation,
      objetivo: item.objetivo,
      status: item.status,
    }));
    // Set survey type selected when editing
    if (item.type) {
      setSurveyTypeSelected(item.type as "generic" | "entity");
    }
    // Load questions from item when viewing/editing
    if (item.questions && item.questions.length > 0) {
      setSelectedQuestions(normalizeByType(item.questions));
    } else {
      setSelectedQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);
  
  // If user is Organization, automatically set type to "entity" and show form
  useEffect(() => {
    if (mode === "create" && user?.role === "Organization" && !surveyTypeSelected) {
      setSurveyTypeSelected("entity");
      setFormData(prev => ({
        ...prev,
        type: "entity",
        organizationId: user.organizationId || "",
      }));
    }
  }, [mode, user?.role, user?.organizationId, surveyTypeSelected]);

  const [entities, setEntities] = useState<Entity[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [allActiveEntities, setAllActiveEntities] = useState<Entity[]>([]);
  const [loadingActiveEntities, setLoadingActiveEntities] = useState(false);

  const [showTranslateTitle, setShowTranslateTitle] = useState(false);
  const [showTranslateDescription, setShowTranslateDescription] =
    useState(false);
  const [loadingTitle, setLoadingTitle] = useState(false);
  const [loadingDescription, setLoadingDescription] = useState(false);

  // Estados para manejo de preguntas
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const normalizeByType = (qs: Question[]) => {
    const generics = qs.filter((q) => q.isGeneric);
    const specifics = qs.filter((q) => !q.isGeneric);
    const out = [...generics, ...specifics].map((q, i) => ({
      ...q,
      order: i + 1,
    }));
    return out;
  };

  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>(
    item?.questions ? normalizeByType(item.questions) : []
  );

  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Cargar todas las entidades activas cuando es genérica para mostrar el conteo
  useEffect(() => {
    if (formData.type === "generic" && token && user?.role === "Administrator") {
      setLoadingActiveEntities(true);
      getEntities(token)
        .then((data) => {
          // Filtrar solo entidades activas (status === 1)
          const activeEntities = data.filter(e => e.status === 1);
          setAllActiveEntities(activeEntities);
        })
        .catch(() => {
          setAllActiveEntities([]);
        })
        .finally(() => setLoadingActiveEntities(false));
    } else {
      setAllActiveEntities([]);
    }
  }, [formData.type, token, user?.role]);

  useEffect(() => {
    if (formData.type === "entity" && token) {
      setLoadingEntities(true);
      getEntities(token)
        .then((data) => {
          setEntities(data);
          // If user is Organization, ensure their organization is selected
          if (user?.role === "Organization" && user.organizationId) {
            const userOrg = data.find(e => e.id === user.organizationId);
            if (userOrg && !formData.organizationId) {
              setFormData(prev => ({
                ...prev,
                organizationId: user.organizationId || "",
              }));
            }
          }
        })
        .catch(() => {
          setEntities([]);
        })
        .finally(() => setLoadingEntities(false));
    }
  }, [formData.type, formData.organizationId, token, isEditing, user?.role, user?.organizationId]);

  // Cargar preguntas disponibles
  useEffect(() => {
    if (!token) return;
    
    // Si es genérica, solo cargar preguntas genéricas
    if (formData.type === "generic") {
      setLoadingQuestions(true);
      getQuestions(token, { isGeneric: true, includeAudioFiles: false })
        .then((questions) => {
          setAvailableQuestions(questions);
        })
        .catch(() => {
          setAvailableQuestions([]);
        })
        .finally(() => setLoadingQuestions(false));
      return;
    }
    
    // Si es entity, necesitamos organizationId para cargar preguntas específicas
    if (formData.type === "entity") {
      if (!formData.organizationId) {
        // Si no hay organizationId seleccionada, solo cargar genéricas
        setLoadingQuestions(true);
        getQuestions(token, { isGeneric: true, includeAudioFiles: false })
          .then((questions) => {
            setAvailableQuestions(questions);
          })
          .catch(() => {
            setAvailableQuestions([]);
          })
          .finally(() => setLoadingQuestions(false));
        return;
      }
      
      // Cargar preguntas genéricas Y específicas de la organización
      setLoadingQuestions(true);
      Promise.all([
        getQuestions(token, { isGeneric: true, includeAudioFiles: false }),
        getQuestions(token, { organizationId: formData.organizationId as string, includeAudioFiles: false })
      ])
        .then(([genericQuestions, specificQuestions]) => {
          // Combinar ambas listas, evitando duplicados por ID
          const combined = [...genericQuestions];
          const genericIds = new Set(genericQuestions.map(q => q.id));
          specificQuestions.forEach(q => {
            if (!genericIds.has(q.id)) {
              combined.push(q);
            }
          });
          setAvailableQuestions(combined);
        })
        .catch(() => {
          setAvailableQuestions([]);
        })
        .finally(() => setLoadingQuestions(false));
      return;
    }
    
    // Por defecto, no cargar nada si no hay tipo seleccionado
    setAvailableQuestions([]);
  }, [token, formData.type, formData.organizationId, isEditing, item?.id]);

  const addQuestionToSurvey = (question: Question) => {
    if (selectedQuestions.find((q) => q.id === question.id)) return;

    // Si la encuesta es genérica, solo permitir preguntas genéricas
    if (formData.type === "generic" && !question.isGeneric) {
      setNotification({
        type: "warning",
        title: translations.error || "Error",
        message: translations.genericSurveyOnlyGenericQuestions || "Las encuestas genéricas solo pueden contener preguntas genéricas",
      });
      return;
    }

    if (question.isGeneric) {
      const firstSpecificIdx = selectedQuestions.findIndex((q) => !q.isGeneric);
      const insertAt =
        firstSpecificIdx === -1 ? selectedQuestions.length : firstSpecificIdx;
      const next = [...selectedQuestions];
      next.splice(insertAt, 0, { ...question });
      setSelectedQuestions(normalizeByType(next));
    } else {
      setSelectedQuestions(
        normalizeByType([...selectedQuestions, { ...question }])
      );
    }
  };

  const removeQuestionFromSurvey = (questionId: string) => {
    setSelectedQuestions((prev) =>
      normalizeByType(prev.filter((q) => q.id !== questionId))
    );
  };

  const canMoveUp = (i: number) =>
    i > 0 &&
    selectedQuestions[i - 1].isGeneric === selectedQuestions[i].isGeneric;

  const canMoveDown = (i: number) =>
    i < selectedQuestions.length - 1 &&
    selectedQuestions[i + 1].isGeneric === selectedQuestions[i].isGeneric;

  const moveQuestionUp = (index: number) => {
    if (!canMoveUp(index)) return;
    const next = [...selectedQuestions];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setSelectedQuestions(normalizeByType(next));
  };

  const moveQuestionDown = (index: number) => {
    if (!canMoveDown(index)) return;
    const next = [...selectedQuestions];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setSelectedQuestions(normalizeByType(next));
  };

  async function translateTextToAllLanguages(text: string) {
    return translateTextToAll({
      text,
      token: token!,
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};

    if (!formData.title_es.trim())
      errors.title_es = translations.titleSpanishRequired;
    if (!formData.title_ca.trim())
      errors.title_ca = translations.titleCatalanRequired;
    if (!formData.description_es)
      errors.description_es = translations.descriptionSpanishRequired;
    if (!formData.description_ca)
      errors.description_ca = translations.descriptionCatalanRequired;
    if (formData.type === "entity" && !formData.organizationId) {
      errors.organizationId = translations.mustSelectEntity;
    }
    if (formData.objetivo === 0) {
      errors.objetivo = translations.goalMustBeGreater;
    }
    if (!formData.objetivo) {
      errors.objetivo = translations.goalRequired;
    }
    if (selectedQuestions.length === 0) {
      errors.questions = translations.surveyMustHaveQuestions || "La encuesta debe tener al menos una pregunta";
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      if (errors.questions) {
        setNotification({
          type: "error",
          title: translations.errorCreatingSurvey || "Error",
          message: errors.questions,
        });
      }
      return;
    }
    setIsSaving(true);
    const normalized = sortQuestionsByTypeThenOrder(selectedQuestions);

    const assignments = normalized.map((q, idx) => ({
      questionId: q.id,
      order: idx + 1,
    }));
    // Transform formData to match Survey type
    /* const basePayload: Omit<
      Survey,
      "createdAt" | "updatedAt" | "organizationName" | "id"
    > = {
      ...(isEditing && { id: item.id }), // Add id for updateSurvey
      title: {
        es: formData.title_es,
        ca: formData.title_ca,
      },
      description: {
        es: formData.description_es || "",
        ca: formData.description_ca || "",
      },
      type: formData.type,
      organizationId:
        formData.type === "entity" ? formData.organizationId : undefined,
      defaultLanguage: formData.defaultLanguage,
      questions: assignments,
      status: formData.status,
      orientation: formData.orientation,
      objetivo: formData.objetivo,
      IsCodeActive: formData.IsCodeActive,
    };*/
    const basePayload: SurveyWriteBase = {
      title: {
        es: formData.title_es,
        ca: formData.title_ca,
        ...(formData.title_en && { en: formData.title_en }),
        ...(formData.title_ar && { ar: formData.title_ar }),
      } as { es: string; ca: string; en?: string; ar?: string },
      description: {
        es: formData.description_es || "",
        ca: formData.description_ca || "",
        ...(formData.description_en && { en: formData.description_en }),
        ...(formData.description_ar && { ar: formData.description_ar }),
      } as { es: string; ca: string; en?: string; ar?: string },
      type: formData.type,
      organizationId:
        formData.type === "entity" ? formData.organizationId : undefined,
      defaultLanguage: formData.defaultLanguage,
      status: formData.status,
      orientation: formData.orientation,
      objetivo: formData.objetivo,
      IsCodeActive: formData.IsCodeActive,
      questions: selectedQuestions.length ? assignments : undefined,
    };
    let response: Survey | ErrorResponse | EmptyResponse;

    try {
      if (isEditing && item) {
        const surveyPayload: UpdateSurveyPayload = {
          id: item.id,
          ...basePayload,
        };
        response = await updateSurvey(item.id, surveyPayload, token!);
      } else {
        response = await createSurvey(basePayload, token!);
        if ("error" in response) {
          setNotification({
            type: "error",
            title: translations.errorCreatingSurvey,
            message: response.message,
          });
          return;
        }
      }

      if ("error" in response) {
        console.error("Error creating/updating survey:", response.message);
        setNotification({
          type: "error",
          title: `${translations.errorCreatingUpdating} ${
            isEditing ? translations.updating : translations.creating
          } ${translations.survey}`,
          message: response.message,
        });
        return;
      }

      // El backend maneja automáticamente la asignación y desasignación de preguntas
      // a través del payload enviado en createSurvey/updateSurvey, por lo que no
      // necesitamos actualizar las preguntas manualmente
      
      // Invalidar queries y refrescar datos
      await queryClient.invalidateQueries({ queryKey: ["surveys"] });
      await queryClient.invalidateQueries({ queryKey: ["questions"] });

      setNotification({
        type: "success",
        title: `${translations.survey} ${
          isEditing ? translations.updated : translations.created
        } ${translations.correctly}`,
        message: undefined,
      });
      onClose();
    } catch (error) {
      console.error("Error updating questions surveyId:", error);
      setNotification({
        type: "warning",
        title: `${translations.survey} ${
          isEditing ? translations.updated : translations.created
        } ${translations.correctly}`,
        message: translations.errorUpdatingQuestions,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl max-w-sm w-full mx-4">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              <p className="text-lg font-medium text-gray-900">
                {translations.saving}
              </p>
              <p className="text-sm text-gray-500 text-center">
                {translations.saving}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <Header>
        <div className="flex items-center gap-4">
          {isViewingOrEditing && (
            <button
              onClick={() => {
                // Navegar primero, la invalidación se hará cuando se monte el componente del listado
                navigate("/surveys");
              }}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-gray-700"
              title={translations.back || "Volver"}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === "create"
                ? translations.newSurvey
                : mode === "show"
                ? translations.viewSurvey || "Ver Encuesta"
                : translations.editSurvey || "Editar Encuesta"}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditing
                ? translations.modifySurveyData
                : mode === "show"
                ? translations.viewSurveyDetails || "Detalles de la encuesta"
                : translations.completeSurveyInfo}
            </p>
          </div>
        </div>
      </Header>
      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Survey Type Selector - Initial selection (only for Administrators, not for Organization) */}
          {mode === "create" && !surveyTypeSelected && user?.role === "Administrator" && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                {translations.selectSurveyType || "Selecciona el tipo de encuesta"} *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setSurveyTypeSelected("generic");
                    setFormData(prev => ({
                      ...prev,
                      type: "generic",
                      organizationId: "",
                    }));
                  }}
                  className="p-6 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="font-semibold text-gray-900 mb-2">
                    {translations.generic || "Genérica"}
                  </div>
                  <div className="text-sm text-gray-600">
                    {translations.genericSurveyDescription || "Encuesta genérica para todas las entidades"}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSurveyTypeSelected("entity");
                    setFormData(prev => ({
                      ...prev,
                      type: "entity",
                      organizationId: prev.organizationId || "",
                    }));
                  }}
                  className="p-6 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="font-semibold text-gray-900 mb-2">
                    {translations.entity || "Para entidad"}
                  </div>
                  <div className="text-sm text-gray-600">
                    {translations.entitySurveyDescription || "Encuesta específica para una entidad"}
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Survey Type Display/Changer - Show when type is selected */}
          {surveyTypeSelected && mode !== "show" && (
            <div className="mb-6 pb-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  {translations.surveyType || "Tipo de encuesta"}
                </label>
                {mode === "create" && user?.role === "Administrator" ? (
                  // Administrators can switch between types
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSurveyTypeSelected("generic");
                        setFormData(prev => ({
                          ...prev,
                          type: "generic",
                          organizationId: "", // Clear organization when switching to generic
                        }));
                        // Remove specific questions when switching to generic
                        setSelectedQuestions(prev => 
                          normalizeByType(prev.filter(q => q.isGeneric))
                        );
                        // Clear organizationId errors if any
                        if (formErrors.organizationId) {
                          setFormErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.organizationId;
                            return newErrors;
                          });
                        }
                      }}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        formData.type === "generic"
                          ? "border-blue-500 bg-blue-50 text-blue-700 font-medium"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      {translations.generic || "Genérica"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSurveyTypeSelected("entity");
                        setFormData(prev => ({
                          ...prev,
                          type: "entity",
                          // Keep organizationId if it exists, otherwise keep empty
                          organizationId: prev.organizationId || "",
                        }));
                      }}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        formData.type === "entity"
                          ? "border-blue-500 bg-blue-50 text-blue-700 font-medium"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      {translations.entity || "Para entidad"}
                    </button>
                  </div>
                ) : (
                  // Organization users or edit mode: show type as read-only
                  <div className="px-4 py-2 rounded-lg border-2 border-gray-300 bg-gray-50 text-gray-700 font-medium">
                    {formData.type === "generic" 
                      ? translations.generic || "Genérica"
                      : translations.entity || "Para entidad"}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {formData.type === "generic" 
                  ? translations.genericSurveyDescription || "Encuesta genérica para todas las entidades"
                  : translations.entitySurveyDescription || "Encuesta específica para una entidad"}
              </p>
            </div>
          )}

          {/* Banner informativo sobre códigos */}
          {surveyTypeSelected && mode !== "show" && (
            <div className={`mb-6 p-4 rounded-lg border ${
              formData.type === "generic"
                ? "bg-blue-50 border-blue-200"
                : "bg-green-50 border-green-200"
            }`}>
              {formData.type === "generic" ? (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-blue-800 mb-2">
                      {isEditing ? (
                        translations.genericSurveyEditWarning || "Los cambios afectarán a todas las entidades activas en el sistema"
                      ) : loadingActiveEntities ? (
                        translations.loadingEntities || "Cargando entidades..."
                      ) : (
                        translations.genericSurveyCodeInfo || "Se generará un código único para cada entidad activa en el sistema"
                      )}
                    </p>
                    {!loadingActiveEntities && allActiveEntities.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-blue-700 mb-2">
                          {allActiveEntities.length} {allActiveEntities.length === 1 
                            ? translations.entity || "entidad" 
                            : translations.entities || "entidades"}:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {allActiveEntities.map((entity) => (
                            <span
                              key={entity.id}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                            >
                              {entity.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm text-green-800 mb-2">
                      {isEditing ? (
                        translations.specificSurveyEditWarning || "Los cambios afectarán a la entidad seleccionada"
                      ) : formData.organizationId ? (
                        translations.specificSurveyCodeInfo || "Se generará un solo código para la entidad seleccionada:"
                      ) : (
                        translations.selectEntityToSeeCodeInfo || "Selecciona una entidad para ver la información del código"
                      )}
                    </p>
                    {formData.organizationId && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                            {entities.find(e => e.id === formData.organizationId)?.name || translations.loading || "Cargando..."}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show rest of form only if type is selected or editing/showing */}
          {(!surveyTypeSelected && mode === "create") ? null : (
            <>
          {/* Title */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.title} ({translations.spanish}) *
              </label>
              <input
                placeholder={translations.titleSpanish}
                type="text"
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.title_es ? "border-red-500" : "border-gray-300"
                }`}
                value={formData.title_es}
                onChange={(e) => {
                  setFormData({ ...formData, title_es: e.target.value });
                  setShowTranslateTitle(false);
                  if (formErrors.title_es) {
                    setFormErrors((prev) => ({ ...prev, title_es: "" }));
                  }
                }}
                onBlur={async () => {
                  if (formData.title_es.trim()) {
                    setShowTranslateTitle(true);
                  }
                }}
              />
              {formErrors.title_es && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.title_es}
                </p>
              )}
              {showTranslateTitle && !loadingTitle && mode !== "show" && (
                <p
                  className="text-blue-600 text-sm mt-1 cursor-pointer hover:underline"
                  onClick={async () => {
                    setLoadingTitle(true);
                    try {
                      const translations_result = await translateTextToAllLanguages(
                        formData.title_es
                      );
                      setFormData((prev) => ({
                        ...prev,
                        title_ca: translations_result.ca,
                        title_en: translations_result.en,
                        title_ar: translations_result.ar,
                      }));
                    } catch (error) {
                      console.error("Error translating:", error);
                      setNotification({
                        type: "error",
                        title: translations.errorCreatingSurvey,
                        message: error instanceof Error ? error.message : "Error al traducir",
                      });
                    } finally {
                      setLoadingTitle(false);
                      setShowTranslateTitle(false);
                    }
                  }}
                >
                  {translations.generateTranslation}
                </p>
              )}

              {loadingTitle && (
                <p className="text-sm text-gray-500 mt-1">
                  {translations.translating}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.title} ({translations.catalan}) *
              </label>
              <input
                placeholder={translations.titleCatalan}
                type="text"
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.title_ca ? "border-red-500" : "border-gray-300"
                }`}
                value={formData.title_ca}
                onChange={(e) => {
                  setFormData({ ...formData, title_ca: e.target.value });
                  if (formErrors.title_ca) {
                    setFormErrors((prev) => ({ ...prev, title_ca: "" }));
                  }
                }}
              />
              {formErrors.title_ca && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.title_ca}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.title} ({translations.english || "English"})
              </label>
              <input
                placeholder={translations.titleEnglish || "Write a title in English"}
                type="text"
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.title_en ? "border-red-500" : "border-gray-300"
                }`}
                value={formData.title_en || ""}
                onChange={(e) => {
                  setFormData({ ...formData, title_en: e.target.value });
                  if (formErrors.title_en) {
                    setFormErrors((prev) => ({ ...prev, title_en: "" }));
                  }
                }}
              />
              {formErrors.title_en && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.title_en}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.title} ({translations.arabic || "Árabe"}) 
              </label>
              <input
                placeholder={translations.titleArabic || "Escribe un título en árabe"}
                type="text"
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.title_ar ? "border-red-500" : "border-gray-300"
                }`}
                value={formData.title_ar || ""}
                onChange={(e) => {
                  setFormData({ ...formData, title_ar: e.target.value });
                  if (formErrors.title_ar) {
                    setFormErrors((prev) => ({ ...prev, title_ar: "" }));
                  }
                }}
              />
              {formErrors.title_ar && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.title_ar}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.description} ({translations.spanish})
              </label>
              <textarea
                placeholder={translations.descriptionSpanish}
                rows={3}
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.description_es
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={formData.description_es}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    description_es: e.target.value,
                  });
                  setShowTranslateDescription(false);
                  if (formErrors.description_es) {
                    setFormErrors((prev) => ({
                      ...prev,
                      description_es: "",
                    }));
                  }
                }}
                onBlur={() => {
                  if (formData.description_es && formData.description_es.trim())
                    setShowTranslateDescription(true);
                }}
              />
              {formErrors.description_es && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.description_es}
                </p>
              )}
              {showTranslateDescription &&
                !loadingDescription &&
                mode !== "show" && (
                  <p
                    className="text-blue-600 text-sm mt-1 cursor-pointer hover:underline"
                    onClick={async () => {
                      setLoadingDescription(true);
                      try {
                        const translations_result = await translateTextToAllLanguages(
                          formData.description_es || ""
                        );
                        setFormData((prev) => ({
                          ...prev,
                          description_ca: translations_result.ca,
                          description_en: translations_result.en,
                          description_ar: translations_result.ar,
                        }));
                      } catch (error) {
                        console.error("Error translating description:", error);
                        setNotification({
                          type: "error",
                          title: translations.errorCreatingSurvey,
                          message: error instanceof Error ? error.message : "Error al traducir",
                        });
                      } finally {
                        setLoadingDescription(false);
                        setShowTranslateDescription(false);
                      }
                    }}
                  >
                    {translations.generateTranslation}
                  </p>
                )}

              {loadingDescription && (
                <p className="text-sm text-gray-500 mt-1">
                  {translations.translating}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.description} ({translations.catalan})
              </label>
              <textarea
                placeholder={translations.descriptionCatalan}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.description_ca
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={formData.description_ca}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    description_ca: e.target.value,
                  });
                  if (formErrors.description_ca) {
                    setFormErrors((prev) => ({
                      ...prev,
                      description_ca: "",
                    }));
                  }
                }}
                disabled={mode === "show"}
              />
              {formErrors.description_ca && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.description_ca}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.description} ({translations.english || "English"})
              </label>
              <textarea
                placeholder={translations.descriptionEnglish || "Write a description in English"}
                rows={3}
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.description_en
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={formData.description_en || ""}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    description_en: e.target.value,
                  });
                  if (formErrors.description_en) {
                    setFormErrors((prev) => ({
                      ...prev,
                      description_en: "",
                    }));
                  }
                }}
              />
              {formErrors.description_en && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.description_en}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.description} ({translations.arabic || "Árabe"}) 
              </label>
              <textarea
                placeholder={translations.descriptionArabic || "Escribe una descripción en árabe"}
                rows={3}
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.description_ar
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                value={formData.description_ar || ""}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    description_ar: e.target.value,
                  });
                  if (formErrors.description_ar) {
                    setFormErrors((prev) => ({
                      ...prev,
                      description_ar: "",
                    }));
                  }
                }}
              />
              {formErrors.description_ar && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.description_ar}
                </p>
              )}
            </div>
          </div>

          {/* Idioma, Entidad (si aplica) y Orientación */}
          {formData.type === "entity" ? (
            <>
              {/* Primera fila: Idioma y Entidad */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.defaultLanguage} *
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.defaultLanguage}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        defaultLanguage: e.target.value as "es" | "ca" | "en" | "ar",
                      });
                    }}
                    disabled={mode === "show"}
                  >
                    <option value="es">{translations.spanish}</option>
                    <option value="ca">{translations.catalan}</option>
                    <option value="en">{translations.english}</option>
                    <option value="ar">{translations.arabic}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.selectEntity} *
                  </label>
                  {entities.length === 0 && !loadingEntities && (
                    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                      {roleUser === UserRole.Administrator ? (
                        <>
                          <p className="text-gray-700 mb-2">
                            {translations.noEntitiesAvailable}
                          </p>
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm hover:underline focus:outline-none"
                            onClick={() => {
                              setNotification({
                                type: "info",
                                title: translations.createNewEntity,
                                message: translations.redirectingEntities,
                              });
                              if (onNavigateToEntities) {
                                onNavigateToEntities();
                              }
                            }}
                          >
                            {translations.createNewEntity}
                          </button>
                        </>
                      ) : (
                        <p className="text-gray-700">
                          {translations.noEntitiesOrganization}
                        </p>
                      )}
                    </div>
                  )}
                  {entities.length > 0 && (
                    <select
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        formErrors.organizationId
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={formData.organizationId}
                      onChange={(e) => {
                        const newOrganizationId = e.target.value;
                        // Si cambia la organización, eliminar preguntas específicas de la organización anterior
                        if (formData.organizationId !== newOrganizationId && selectedQuestions.length > 0) {
                          setSelectedQuestions(prev => 
                            normalizeByType(prev.filter(q => q.isGeneric || q.organizationId === newOrganizationId))
                          );
                        }
                        setFormData({
                          ...formData,
                          organizationId: newOrganizationId,
                        });
                        if (formErrors.organizationId) {
                          setFormErrors((prev) => ({
                            ...prev,
                            organizationId: "",
                          }));
                        }
                      }}
                      disabled={
                        roleUser === UserRole.Organization ||
                        mode === "show" ||
                        loadingEntities
                      }
                    >
                      {user?.role !== "Organization" && (
                        <option value="">{translations.selectEntity}</option>
                      )}
                      {entities.map((entity) => (
                        <option key={entity.id} value={entity.id}>
                          {entity.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {formErrors.organizationId && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.organizationId}
                    </p>
                  )}
                </div>
              </div>
              {/* Segunda fila: Orientación */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.surveyOrientation} *
                  </label>
                  <select
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.orientation
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={formData.orientation}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        orientation: e.target.value as "niño" | "familia",
                      });
                      if (formErrors.orientation) {
                        setFormErrors((prev) => ({
                          ...prev,
                          orientation: "",
                        }));
                      }
                    }}
                    disabled={mode === "show"}
                  >
                    <option value="niño">{translations.child}</option>
                    <option value="familia">{translations.family}</option>
                  </select>
                  {formErrors.orientation && (
                    <p className="mt-1 text-sm text-red-600">
                      {formErrors.orientation}
                    </p>
                  )}
                </div>
                <div></div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.defaultLanguage} *
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.defaultLanguage}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      defaultLanguage: e.target.value as "es" | "ca" | "en" | "ar",
                    });
                  }}
                  disabled={mode === "show"}
                >
                  <option value="es">{translations.spanish}</option>
                  <option value="ca">{translations.catalan}</option>
                  <option value="en">{translations.english}</option>
                  <option value="ar">{translations.arabic}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.surveyOrientation} *
                </label>
                <select
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    formErrors.orientation
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  value={formData.orientation}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      orientation: e.target.value as "niño" | "familia",
                    });
                    if (formErrors.orientation) {
                      setFormErrors((prev) => ({
                        ...prev,
                        orientation: "",
                      }));
                    }
                  }}
                  disabled={mode === "show"}
                >
                  <option value="niño">{translations.child}</option>
                  <option value="familia">{translations.family}</option>
                </select>
                {formErrors.orientation && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.orientation}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Objetivo por encuesta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.surveyGoal}
              </label>
              <input
                disabled={mode === "show"}
                type="number"
                min="0"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.objetivo ? "border-red-500" : "border-gray-300"
                }`}
                value={formData.objetivo}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    objetivo: Number(e.target.value),
                  })
                }
                placeholder={translations.enterGoal}
              />
              {formErrors.objetivo && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.objetivo}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.state}
              </label>
              <select
                disabled={mode === "show"}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.status ? "border-red-500" : "border-gray-300"
                }`}
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as
                      | "draft"
                      | "published"
                      | "archived",
                  })
                }
              >
                <option value="draft">{translations.draft}</option>
                <option value="published">{translations.published}</option>
              </select>
              {formErrors.status && (
                <p className="mt-1 text-sm text-red-600">{formErrors.status}</p>
              )}
            </div>
          </div>

          {mode === "show" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.surveyCode}
                </label>
                <input
                  disabled
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     "border-gray-300"`}
                  value={formData.code}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.surveyCodeActivation} *
                </label>
                <select
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    formErrors.IsCodeActive
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  value={String(formData.IsCodeActive)}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      IsCodeActive: e.target.value === "true",
                    });
                    if (formErrors.IsCodeActive) {
                      setFormErrors((prev) => ({
                        ...prev,
                        IsCodeActive: "",
                      }));
                    }
                  }}
                  disabled={mode === "show"}
                >
                  <option value="true">{translations.surveyCodeActive}</option>
                  <option value="false">
                    {translations.surveyCodeDeActive}
                  </option>
                </select>
                {formErrors.IsCodeActive && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.IsCodeActive}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Sección de Preguntas */}
          {mode === "show" ? (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {translations.questionsSection}
              </h3>
              <div className="space-y-3">
                {selectedQuestions.length > 0 ? (
                  <div className="space-y-2">
                    {selectedQuestions.map((question, index) => (
                      <div
                        key={question.id}
                        className="flex items-start space-x-3 p-4 bg-white rounded-lg border border-gray-200"
                      >
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                            {index + 1}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-2">
                            {question.text[formData.defaultLanguage]}
                          </p>
                          <div className="flex items-center space-x-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                !question.isGeneric
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {question.isGeneric
                                ? translations.genericQuestion
                                : translations.specificQuestion}
                            </span>
                            <span className="text-xs text-gray-500">
                              {translations.originalOrder}: {question.order}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {translations.noQuestion}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {translations.questionsSection}
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Preguntas disponibles */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {translations.availableQuestions}
                  </h4>
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                    {loadingQuestions ? (
                      <p className="text-sm text-gray-500">
                        {translations.loadingQuestions}
                      </p>
                    ) : availableQuestions.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        {translations.noQuestionsAvailable}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {availableQuestions
                          .filter(
                            (q) =>
                              !selectedQuestions.find((sq) => sq.id === q.id)
                          )
                          .map((question) => (
                            <div
                              key={question.id}
                              className="flex items-start justify-between p-3 bg-white rounded border border-gray-200 hover:bg-gray-50"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {question.text[formData.defaultLanguage]}
                                </p>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                                    !question.isGeneric
                                      ? "bg-green-100 text-green-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {question.isGeneric
                                    ? translations.genericQuestion
                                    : translations.specificQuestion}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => addQuestionToSurvey(question)}
                                className="ml-2 p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                title={translations.addToSurvey}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Preguntas seleccionadas */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    {translations.selectedQuestions} ({selectedQuestions.length}
                    )
                  </h4>
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                    {selectedQuestions.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        {translations.noQuestion}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {selectedQuestions.map((question, index) => (
                          <div
                            key={question.id}
                            className="flex items-start justify-between p-3 bg-white rounded border border-gray-200"
                          >
                            <div className="flex items-start space-x-2 flex-1 min-w-0">
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex-shrink-0">
                                {index + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                  {question.text[formData.defaultLanguage]}
                                </p>
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                                    !question.isGeneric
                                      ? "bg-green-100 text-green-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {question.isGeneric
                                    ? translations.genericQuestion
                                    : translations.specificQuestion}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1 ml-2">
                              <button
                                type="button"
                                onClick={() => moveQuestionUp(index)}
                                disabled={!canMoveUp(index)}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={translations.moveUp}
                              >
                                <ArrowUp className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveQuestionDown(index)}
                                disabled={!canMoveDown(index)}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={translations.moveDown}
                              >
                                <ArrowDown className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  removeQuestionFromSurvey(question.id)
                                }
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                title={translations.removeFromSurvey}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {mode === "show" ? translations.close : translations.cancel}
            </button>
            {mode !== "show" && surveyTypeSelected && (
              <button
                type="submit"
                disabled={selectedQuestions.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
              >
                {isEditing ? translations.update : translations.create}{" "}
                {translations.survey}
              </button>
            )}
          </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
